import {
  isThisISOWeek,
  isThisYear,
  isToday,
  isTomorrow,
  isYesterday,
} from 'date-fns';
import type React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaMapMarkerAlt, FaRegCalendarAlt } from 'react-icons/fa';
import { MdBusinessCenter } from 'react-icons/md';
import useTimezone from '../../../../hooks/useTimezone';
import type { ConversationMessageDataType } from '../../../../redux/ducks/ChatDucks.types';

export type AppointmentSummaryBlockProps = {
  data: ConversationMessageDataType;
  showChange?: boolean;
  changeReversed?: boolean;
};

const AppointmentSummaryBlock: React.FC<AppointmentSummaryBlockProps> = ({
  data,
  showChange,
  changeReversed,
}) => {
  const { t, i18n } = useTranslation();
  const { currentTimezone } = useTimezone();
  const meta = data?.meta;

  const formatDateLabel = useCallback((ts: number) => {
    const messageDate = new Date(ts);

    let datePart = null;

    if (isToday(messageDate)) {
      datePart = t('static.generic.today');
    } else if (isTomorrow(messageDate)) {
      datePart = t('static.generic.tomorrow');
    } else if (isYesterday(messageDate)) {
      datePart = t('static.generic.yesterday');
    } else if (isThisISOWeek(messageDate)) {
      datePart = t('static.generic.this_week_day', {
        name: Intl.DateTimeFormat(i18n.language, {
          weekday: 'long',
          timeZone: currentTimezone,
        }).format(messageDate),
      });
    } else if (isThisYear(messageDate)) {
      return Intl.DateTimeFormat(i18n.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: currentTimezone,
      }).format(new Date(messageDate));
    } else {
      datePart = Intl.DateTimeFormat(i18n.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: currentTimezone,
      }).format(new Date(messageDate));
    }

    if (datePart !== null) {
      return `${datePart}, ${Intl.DateTimeFormat(i18n.language, {
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
        timeZone: currentTimezone,
      }).format(new Date(messageDate))}`;
    }

    return Intl.DateTimeFormat(i18n.language, {
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
      timeZone: currentTimezone,
    }).format(new Date(messageDate));
  }, []);

  if (!meta) {
    return null;
  }

  return changeReversed ? (
    <div className="flex min-w-full flex-col items-start justify-start gap-3 capitalize">
      <span className="flex min-w-full flex-row">
        <MdBusinessCenter size={24} className="min-w-[32px]" />
        <span className="grow">
          {showChange && meta?.change_request_service && (
            <span className="mr-2 text-slate-500 line-through">
              {meta?.change_request_service.name} (
              {new Intl.NumberFormat(i18n.language, {
                style: 'currency',
                currency: meta?.change_request_service.currency,
              }).format(meta?.change_request_service.price)}
              )
            </span>
          )}
          <span
            className={
              showChange && meta?.change_request_service ? 'font-bold' : ''
            }
          >
            {meta.current_service.name} (
            {new Intl.NumberFormat(i18n.language, {
              style: 'currency',
              currency: meta.current_service.currency,
            }).format(meta.current_service.price)}
            )
          </span>
        </span>
      </span>
      <span className="flex min-w-full flex-row">
        <FaRegCalendarAlt size={22} className="min-w-[32px]" />
        <span className="grow">
          {showChange && meta?.change_request_schedule && (
            <span className="mr-2 capitalize text-slate-500 line-through">
              {formatDateLabel(meta.change_request_schedule)}
            </span>
          )}
          <span
            className={
              showChange && meta?.change_request_schedule
                ? 'font-bold capitalize'
                : 'capitalize'
            }
          >
            {formatDateLabel(meta.current_schedule)}
          </span>
        </span>
      </span>
      {meta?.current_address?.address && (
        <span className="flex min-w-full flex-row">
          <FaMapMarkerAlt size={22} className="min-w-[32px]" />
          <span className="grow">
            {showChange && meta?.change_request_business && (
              <span className="mr-2 text-slate-500 line-through">
                {`${meta.change_request_business.name}`}
              </span>
            )}
            <span
              className={
                showChange && meta?.change_request_business ? 'font-bold' : ''
              }
            >
              {meta?.current_business?.name
                ? `${meta.current_business.name}, ${meta.current_address.address}`
                : meta.current_address.address}
            </span>
          </span>
        </span>
      )}
    </div>
  ) : (
    <div className="flex min-w-full flex-col items-start justify-start gap-3 capitalize">
      <span className="flex min-w-full flex-row">
        <MdBusinessCenter size={24} className="min-w-[32px]" />
        <span className="grow">
          {showChange && meta?.change_request_service && (
            <span className="font-bold">
              {meta?.change_request_service.name} (
              {new Intl.NumberFormat(i18n.language, {
                style: 'currency',
                currency: meta?.change_request_service.currency,
              }).format(meta?.change_request_service.price)}
              )
            </span>
          )}
          <span
            className={
              showChange && meta?.change_request_service
                ? 'ml-2 text-slate-500 line-through'
                : ''
            }
          >
            {meta.current_service.name} (
            {new Intl.NumberFormat(i18n.language, {
              style: 'currency',
              currency: meta.current_service.currency,
            }).format(meta.current_service.price)}
            )
          </span>
        </span>
      </span>
      <span className="flex min-w-full flex-row">
        <FaRegCalendarAlt size={22} className="min-w-[32px]" />
        <span className="grow">
          {showChange && meta?.change_request_schedule && (
            <span className="font-bold capitalize">
              {formatDateLabel(meta.change_request_schedule)}
            </span>
          )}
          <span
            className={
              showChange && meta?.change_request_schedule
                ? 'ml-2 capitalize text-slate-500 line-through'
                : 'capitalize'
            }
          >
            {formatDateLabel(meta.current_schedule)}
          </span>
        </span>
      </span>
      {meta?.current_address?.address && (
        <span className="flex min-w-full flex-row">
          <FaMapMarkerAlt size={22} className="min-w-[32px]" />
          <span className="grow">
            {showChange && meta?.change_request_business && (
              <span className="font-bold">
                {`${meta.change_request_business.name}`}
              </span>
            )}
            <span
              className={
                showChange && meta?.change_request_business
                  ? 'ml-2 text-slate-500 line-through'
                  : ''
              }
            >
              {meta?.current_business?.name
                ? `${meta.current_business?.name}, ${meta.current_address.address}`
                : meta.current_address.address}
            </span>
          </span>
        </span>
      )}
    </div>
  );
};

export default AppointmentSummaryBlock;
